import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi, AuthResponse, LoginRequest, RegisterRequest, ApiError, onAuthError } from '../services';

// User type
interface User {
  id: string;
  email: string;
}

// Auth state type
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Auth context type
interface AuthContextType extends AuthState {
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token storage keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Save auth data to storage
  const saveAuthData = useCallback((response: AuthResponse) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify({
      id: response.userId,
      email: response.email,
    }));
  }, []);

  // Clear auth data from storage
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Clear session flags so modal shows again on next login
    sessionStorage.removeItem('checkinModalShown');
  }, []);

  // Handle logout (used by both manual logout and auth error)
  const handleLogout = useCallback(() => {
    clearAuthData();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, [clearAuthData]);

  // Listen for global auth errors (401s from API calls)
  useEffect(() => {
    const unsubscribe = onAuthError(() => {
      handleLogout();
    });
    return unsubscribe;
  }, [handleLogout]);

  // Validate token on mount by calling the API
  useEffect(() => {
    const validateStoredAuth = async () => {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (!accessToken || !storedUser) {
        // No stored credentials - not authenticated
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        // Validate token by fetching user profile
        const userProfile = await authApi.validateToken();
        setState({
          user: { id: userProfile.id, email: userProfile.email },
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        // Token is invalid or expired - try to refresh
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (storedRefreshToken) {
          try {
            const response = await authApi.refresh({ refreshToken: storedRefreshToken });
            saveAuthData(response);
            setState({
              user: { id: response.userId, email: response.email },
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          } catch {
            // Refresh also failed - clear everything
          }
        }

        // Clear invalid auth data
        clearAuthData();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    validateStoredAuth();
  }, [clearAuthData, saveAuthData]);

  // Login function
  const login = useCallback(async (data: LoginRequest): Promise<void> => {
    const response = await authApi.login(data);
    saveAuthData(response);
    setState({
      user: { id: response.userId, email: response.email },
      isAuthenticated: true,
      isLoading: false,
    });
  }, [saveAuthData]);

  // Register function
  const register = useCallback(async (data: RegisterRequest): Promise<void> => {
    const response = await authApi.register(data);
    saveAuthData(response);
    setState({
      user: { id: response.userId, email: response.email },
      isAuthenticated: true,
      isLoading: false,
    });
  }, [saveAuthData]);

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors - we'll clear local state anyway
    } finally {
      handleLogout();
    }
  }, [handleLogout]);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefreshToken) return false;

      const response = await authApi.refresh({ refreshToken: storedRefreshToken });
      saveAuthData(response);
      setState({
        user: { id: response.userId, email: response.email },
        isAuthenticated: true,
        isLoading: false,
      });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        handleLogout();
      }
      return false;
    }
  }, [saveAuthData, handleLogout]);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
